import * as Sentry from '@sentry/react';

/**
 * KB-3467 — the two IndexedDB doors that auth boot still awaited after KB-3425,
 * why the heartbeat door is bounded at the OPEN, and why the App Check door is
 * NOT bounded at all.
 *
 * ## The doors (firebase 12.18.0, measured on the live prod bundle — ticket AC-1)
 *
 * Every auth request awaits `_getAdditionalHeaders()` (`@firebase/auth` 1.13.5
 * `index-CvXU3_1x.js:3166`), which awaits two things this app does not own:
 *
 * - the **heartbeat** header — `@firebase/app`'s `HeartbeatStorageImpl` runs
 *   `@firebase/util`'s `validateIndexedDBOpenable`, which opens
 *   {@link FIREBASE_IDB_OPENABLE_PROBE}, then opens
 *   {@link FIREBASE_HEARTBEAT_IDB}. The App Check exchange awaits this header
 *   too, so a stall here also stops App Check from ever minting.
 * - the **App Check** token — `@firebase/app-check`'s `getToken` awaits the
 *   cached token read, a raw open of {@link FIREBASE_APP_CHECK_IDB}.
 *
 * Each open has `success` / `error` / `upgradeneeded` handlers and no deadline.
 * A store that answers with no event at all (KB-3386 measured one on the sibling
 * origin, silent for 8001 ms) leaves each await pending. Auth's own 30 s
 * `NetworkTimeout` then fails every request with `network-request-failed`,
 * for the rest of the page load, because both SDKs cache the pending promise.
 *
 * ## Why here, and why an ERROR
 *
 * KB-3425's rule binds: a bound must never produce a value its caller can
 * mistake for ground truth (`authPersistence.ts` records the three designs that
 * broke it). So nothing is bounded on auth's request path — no header is
 * invented, no token is faked, and no await auth makes can reject on our timer.
 *
 * Instead the heartbeat door's own request is failed, with a `TimeoutError`,
 * after {@link IDB_OPEN_BOUND_MS}. The SDK already handles an open that errors,
 * and what it does with one is true and non-destructive:
 * `validateIndexedDBOpenable` rejects → the environment check is `false` →
 * heartbeats are kept in memory and still sent; nothing is written or deleted.
 * "Storage unavailable" is the truth about a store that did not answer.
 * When the probe answers but {@link FIREBASE_HEARTBEAT_IDB} itself expires,
 * the path differs: `readHeartbeatsFromIndexedDB` returns `undefined` and
 * `read()` yields `{heartbeats: []}` — "none stored", which is false if unsent
 * heartbeats sit in the store. It is inert: the rejected `dbPromise` is cached,
 * so no write-back lands, and the only cost is past days' heartbeats missing
 * from this page load's header (telemetry, not auth).
 *
 * One case it does change, stated because it looks like a sign-out caused by
 * this bound (ruled acceptable on KB-3467, 2026-09-18). Heartbeat door wedged,
 * NO App Check token cached, attestation refused: past the expiry App Check
 * mints, is refused, and the SDK clears the user at ~2 s. That is exactly what
 * a healthy profile gets today, at ~50 ms, from the same facts — every value
 * involved is true. Unbounded, the same user sits behind a 30 s stall and is
 * "kept" with every auth request failing. Pinned against the healthy baseline:
 * indexedDbOpenBound.sdk.refusedHeartbeatHeld / .refusedBaseline.test.ts.
 *
 * Expiry stays distinguishable from a real answer: the error is a
 * `TimeoutError` naming the store and the bound, and every expiry is recorded
 * ({@link getIndexedDbOpenExpiries}, a Sentry breadcrumb, a console warning).
 *
 * ## ⛔ Why the App Check store is NOT bounded (KB-3467 round 1, B1; ruled B)
 *
 * Failing {@link FIREBASE_APP_CHECK_IDB}'s open was tried and reproduced as a
 * sign-out. App Check's `getDBPromise` rejection makes `readTokenFromStorage`
 * return `undefined` — "no cached token" — which is FALSE when a valid token
 * sits in the store. App Check then mints; if that mint is refused (the
 * KB-3352 403), auth sends the SDK's dummy token, enforced Identity Toolkit
 * refuses the boot lookup, and `reloadAndSetCurrentUserOrClear` clears the
 * persisted user at ~2 s. Unbounded, the same user is kept (auth's 30 s
 * `network-request-failed`). That is exactly the KB-3425 failure — a bound
 * whose product the SDK takes as ground truth — so a wedged App Check store
 * keeps today's 30 s stall, with the user kept and `/logout`'s rescue at 8 s.
 * Bounding it needs a read that can tell "store unavailable" from "no token";
 * that is a follow-up, not this module. `/logout` still deletes the store.
 *
 * A late answer is never delivered: the SDK's promise has already settled, and
 * a connection that opens after expiry is closed here rather than leaked.
 */

/** `@firebase/util` `validateIndexedDBOpenable`'s `DB_CHECK_NAME`. */
export const FIREBASE_IDB_OPENABLE_PROBE = 'validate-browser-context-for-indexeddb-analytics-module';
/** `@firebase/app`'s heartbeat store `DB_NAME`. */
export const FIREBASE_HEARTBEAT_IDB = 'firebase-heartbeat-database';
/** `@firebase/app-check`'s token store `DB_NAME`. */
export const FIREBASE_APP_CHECK_IDB = 'firebase-app-check-database';

/** The heartbeat door's two opens. Not {@link FIREBASE_APP_CHECK_IDB} — see the header. */
export const BOUNDED_IDB_NAMES: readonly string[] = [
  FIREBASE_IDB_OPENABLE_PROBE,
  FIREBASE_HEARTBEAT_IDB,
];

/**
 * Healthy opens answer in milliseconds; a wedged store is silent at 8 s
 * (KB-3386). The heartbeat door can pay this twice in sequence — the probe,
 * then its own store — and the App Check mint waits on the heartbeat header,
 * so the worst case is ~2 × this plus an exchange and a lookup, which must fit
 * inside AuthContext's 8000 ms `BOOTSTRAP_BUDGET_MS`. Same value, for the same
 * reason, as `/logout`'s `IDB_DELETE_TIMEOUT_MS`.
 */
export const IDB_OPEN_BOUND_MS = 2000;

export interface IdbOpenExpiry {
  name: string;
  boundMs: number;
  at: number;
}

const expiries: IdbOpenExpiry[] = [];

/** Every open this page load failed on the bound, oldest first. */
export function getIndexedDbOpenExpiries(): readonly IdbOpenExpiry[] {
  return expiries.slice();
}

export function __resetIndexedDbOpenExpiriesForTests(): void {
  expiries.length = 0;
}

const INSTALLED = Symbol.for('kb-3467/indexeddb-open-bound');

interface InstallOptions {
  factory?: IDBFactory;
  boundMs?: number;
  names?: readonly string[];
  setTimer?: (cb: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

/**
 * Wrap `indexedDB.open` so an open of a {@link BOUNDED_IDB_NAMES} store that
 * has not answered within the bound fails with a `TimeoutError`. Every other
 * name is passed straight through, untouched.
 *
 * Must run before `initializeApp`: the first `_getProvider` call instantiates
 * the heartbeat service, whose constructor starts the open.
 *
 * Returns `false` (and changes nothing) where IndexedDB is absent, throws on
 * access, or refuses the wrapper — the SDK's own open stays as it was.
 */
export function installIndexedDbOpenBound(options: InstallOptions = {}): boolean {
  let factory: IDBFactory | undefined;
  try {
    factory = options.factory ?? globalThis.indexedDB;
  } catch {
    return false;
  }
  if (!factory || typeof factory.open !== 'function') return false;
  if ((factory as unknown as Record<symbol, unknown>)[INSTALLED]) return true;

  const boundMs = options.boundMs ?? IDB_OPEN_BOUND_MS;
  const bounded = new Set(options.names ?? BOUNDED_IDB_NAMES);
  const setTimer = options.setTimer ?? ((cb, ms) => setTimeout(cb, ms));
  const clearTimer = options.clearTimer ?? ((handle) => clearTimeout(handle as never));
  const realOpen = factory.open;

  function open(this: IDBFactory, name: string, ...rest: [version?: number]): IDBOpenDBRequest {
    const request = realOpen.apply(this, [name, ...rest] as [string, number?]);
    if (bounded.has(name)) armBound(request, name, boundMs, setTimer, clearTimer);
    return request;
  }

  // This runs while firebaseService.ts is evaluated, so a throw here would stop
  // the whole app from loading — far worse than the stall it exists to bound.
  // A factory that refuses the property leaves the SDK's own open in place.
  try {
    Object.defineProperty(factory, 'open', { value: open, configurable: true, writable: true });
    Object.defineProperty(factory, INSTALLED, { value: true });
  } catch (err) {
    console.warn('[idb-bound] could not wrap indexedDB.open; the heartbeat opens stay unbounded', err);
    return false;
  }
  return true;
}

function armBound(
  request: IDBOpenDBRequest,
  name: string,
  boundMs: number,
  setTimer: (cb: () => void, ms: number) => unknown,
  clearTimer: (handle: unknown) => void,
): void {
  let answered = false;
  let expired = false;
  let handle: unknown = null;

  // Registered before the caller gets the request, so these run before any
  // handler the SDK attaches. `blocked` and `upgradeneeded` are not answers:
  // the open is still in flight, and `blocked` can last forever.
  request.addEventListener('success', () => {
    if (expired) {
      // The SDK's promise already settled on the expiry; nobody will ever use
      // or close this connection, and an open one blocks later deletes and
      // version changes of this store.
      try {
        request.result.close();
      } catch {
        // Nothing to close.
      }
      return;
    }
    answered = true;
    clearTimer(handle);
  });
  request.addEventListener('error', () => {
    if (expired) return;
    answered = true;
    clearTimer(handle);
  });
  const errorListeners = recordErrorListeners(request);

  handle = setTimer(() => {
    if (answered) return;
    expired = true;
    const error = new DOMException(
      `indexedDB.open("${name}") did not answer within ${boundMs} ms`,
      'TimeoutError',
    );
    // The real `error` getter throws InvalidStateError while the request is
    // pending, and both SDKs read it in their error handlers — an own property
    // shadows it for this request only.
    try {
      Object.defineProperty(request, 'error', { configurable: true, get: () => error });
    } catch {
      // A request that refuses the shadow must still hear of the expiry.
    }
    expiries.push({ name, boundMs, at: Date.now() });
    console.warn(`[idb-bound] ${error.message}; continuing without this store for this page load`);
    Sentry.addBreadcrumb({
      category: 'auth-bootstrap',
      level: 'warning',
      message: `indexedDB open bound expired: ${name}`,
      data: { boundMs },
    });
    failOpen(request, new Event('error'), errorListeners);
  }, boundMs);
}

type ErrorListener = EventListenerOrEventListenerObject;

/**
 * PRO-WEB-3: a prerender-style runtime hands back a request that has
 * `addEventListener` but no `dispatchEvent`, so the expiry cannot be dispatched
 * and the handlers have to be called by hand. The two doors listen differently:
 * `@firebase/util`'s openable probe sets `onerror`, while the heartbeat store open
 * goes through `idb`'s `openDB`, which only calls `addEventListener('error')`. So
 * the listeners are recorded as they are added — only on a request that lacks
 * `dispatchEvent`, so every other request is left exactly as it was.
 *
 * Limits, on purpose: a request that HAS `dispatchEvent` but whose dispatch throws
 * is not recorded, so there only `onerror` is called; and a listener removed or
 * aborted before expiry is still called, which no consumer on these two doors
 * reaches (`idb` removes its listeners only after the request settles, when the
 * timer is already cleared).
 *
 * This runs inside `indexedDB.open` on the app-boot path, so it must not throw:
 * even the read of `dispatchEvent` is inside the `try`.
 */
function recordErrorListeners(request: IDBOpenDBRequest): ErrorListener[] {
  const recorded: ErrorListener[] = [];
  try {
    if (typeof request.dispatchEvent === 'function') return recorded;
    const add = request.addEventListener.bind(request) as EventTarget['addEventListener'];
    request.addEventListener = ((type: string, listener: ErrorListener | null, options?: boolean | AddEventListenerOptions) => {
      if (type === 'error' && listener) recorded.push(listener);
      return add(type, listener, options);
    }) as unknown as typeof request.addEventListener;
  } catch {
    // A request that refuses the wrapper still gets `onerror` called on expiry.
  }
  return recorded;
}

/**
 * Fail the open with `event`: dispatch it, and where that is missing or throws,
 * call `onerror` and the recorded listeners directly. Each handler is isolated,
 * as a native dispatch isolates them: one that throws is logged and the next
 * still runs, and nothing escapes the timer.
 */
function failOpen(request: IDBOpenDBRequest, event: Event, listeners: readonly ErrorListener[]): void {
  try {
    request.dispatchEvent(event);
    return;
  } catch {
    // No usable dispatchEvent: deliver by hand below.
  }
  const handlers: unknown[] = [request.onerror, ...listeners];
  for (const handler of handlers) {
    try {
      if (typeof handler === 'function') handler.call(request, event);
      else if (typeof (handler as EventListenerObject | null)?.handleEvent === 'function') {
        (handler as EventListenerObject).handleEvent(event);
      }
    } catch (err) {
      console.warn('[idb-bound] an open error handler threw', err);
    }
  }
}
